import Vue from "vue";
import Router from "vue-router";
import Home from "../views/Home.vue";
import store from "@/store";
import baseConfig from "@/store/baseConfig.js";
import Analytics from "@/mixins/analytics";

Vue.use(Router);

let cmeOnly = baseConfig.CmeOnly || false;

let routes = [];

// If this is a normal meeting app, set the standard route list
if (!cmeOnly) {
  routes = [
    /* Hard Coded */
    {
      path: "/",
      name: "Home",
      component: Home,
      meta: {
        analyticsName: "explore",
      },
    },
    {
      path: "/login",
      name: "Login",
      component: () =>
        import(/* webpackChunkName: "Login" */ "../views/Login.vue"),
      meta: {
        analyticsName: "login",
      },
    },
    {
      path: "/notes",
      name: "Notes",
      component: () =>
        import(/* webpackChunkName: "Notes" */ "../views/Notes.vue"),
      meta: {
        requiresAuth: true,
        analyticsName: "notes",
      },
    },
    {
      path: "/secretPage",
      name: "SecretPage",
      component: () =>
        import(/* webpackChunkName: "SecretPage" */ "../views/SecretPage.vue"),
      meta: {
        requiresAuth: true,
        analyticsName: "secret_web_page",
      },
    },

    /* Sections from Firestore */
    {
      path: "/ask-me",
      name: "AskMe",
      component: () =>
        import(/* webpackChunkName: "AskMe" */ "../views/AskMe.vue"),
      meta: {
        analyticsName: "ask_me",
      },
    },
    {
      path: "/maps",
      name: "Maps",
      component: () =>
        import(/* webpackChunkName: "Maps" */ "../views/Maps.vue"),
      meta: {
        analyticsName: "maps",
      },
    },
    {
      path: "/maps/:id",
      name: "MapsId",
      component: () =>
        import(/* webpackChunkName: "Maps" */ "../views/Maps.vue"),
      meta: {
        analyticsName: "map_details",
      },
    },
    {
      path: "/cctf",
      name: "CCTF",
      component: () =>
        import(/* webpackChunkName: "CCTF" */ "../views/CCTF.vue"),
      meta: {
        analyticsName: "cctf",
      },
    },
    {
      path: "/cme",
      name: "CME",
      component: () => import(/* webpackChunkName: "CME" */ "../views/CME.vue"),
      meta: {
        requiresAuth: true,
        hideSponsorInfo: true,
        analyticsName: "cme",
      },
    },
    {
      path: "/cme/:sessionCode",
      name: "CMESurvey",
      component: () =>
        import(/* webpackChunkName: "CMESurvey" */ "../views/CMESurvey.vue"),
      meta: {
        requiresAuth: true,
        hideSponsorInfo: true,
        analyticsName: "cme_details",
      },
    },
    {
      path: "/CMEDelete/:id",
      name: "CMEDelete",
      component: () =>
        import(/* webpackChunkName: "CMEDelete" */ "../views/CMEDelete.vue"),
      meta: {
        requiresAuth: true,
        hideSponsorInfo: true,
        analyticsName: "cme_delete",
      },
    },
    {
      path: "/handouts",
      name: "Handouts",
      component: () =>
        import(/* webpackChunkName: "Handouts" */ "../views/Handouts.vue"),
      meta: {
        requiresAuth: true,
        analyticsName: "handouts",
        hideSponsorInfo: true,
      },
    },

    {
      path: "/sessions",
      name: "Sessions",
      component: () =>
        import(/* webpackChunkName: "Sessions" */ "../views/Sessions.vue"),
      meta: {
        hideSponsorInfo: true,
        analyticsName: "sessions",
      },
    },
    {
      path: "/sessions/:id",
      name: "SessionDetails",
      component: () =>
        import(
          /* webpackChunkName: "SessionDetails" */ "../views/SessionDetails.vue"
        ),
      meta: {
        hideSponsorInfo: true,
        analyticsName: "session_details",
      },
    },
    {
      path: "/sessions/:sessionCode",
      name: "SessionDetailsCode",
      component: () =>
        import(
          /* webpackChunkName: "SessionDetails" */ "../views/SessionDetails.vue"
        ),
      meta: {
        hideSponsorInfo: true,
        analyticsName: "session_details",
      },
    },
    {
      path: "/speakers",
      name: "Speakers",
      component: () =>
        import(/* webpackChunkName: "Speakers" */ "../views/Speakers.vue"),
      meta: {
        hideSponsorInfo: true,
        analyticsName: "speakers",
      },
    },
    {
      path: "/speakers/:id",
      name: "SpeakerDetails",
      component: () =>
        import(
          /* webpackChunkName: "SpeakerDetails" */ "../views/SpeakerDetails.vue"
        ),
      meta: {
        hideSponsorInfo: true,
        analyticsName: "speaker_details",
      },
    },

    /* Default/404 */
    {
      path: "/404",
      alias: "/:pathMatch(.*)*",
      name: "NotFound",
      component: () =>
        import(
          /*webpackChunkName: "NotFound" */
          "../views/NotFound.vue"
        ),
      meta: {
        analyticsName: "not_found",
      },
    },
  ];
}
// If this is a CME only application (VMX), then set up these routes
else {
  routes = [
    /* Hard Coded */
    {
      path: "/",
      name: "CME",
      component: () => import(/* webpackChunkName: "CME" */ "../views/CME.vue"),
      meta: {
        requiresAuth: true,
        analyticsName: "cme",
      },
    },
    {
      path: "/login",
      name: "Login",
      component: () =>
        import(/* webpackChunkName: "Login" */ "../views/Login.vue"),
      meta: {
        analyticsName: "login",
      },
    },
    {
      path: "/cme",
      redirect: { name: "CME" },
    },
    {
      path: "/cme/:id",
      name: "CMESurvey",
      component: () =>
        import(/* webpackChunkName: "CMESurvey" */ "../views/CMESurvey.vue"),
      meta: {
        requiresAuth: true,
        analyticsName: "cme_details",
      },
    },
    {
      path: "/CMEDelete/:id",
      name: "CMEDelete",
      component: () =>
        import(/* webpackChunkName: "CMEDelete" */ "../views/CMEDelete.vue"),
      meta: {
        requiresAuth: true,
        hideSponsorInfo: true,
        analyticsName: "cme_delete",
      },
    },
  ];
}

let router = new Router({
  routes: routes,
  mode: "history",
});

// Route guards
router.beforeEach((to, from, next) => {
  let isAuthenticated = store.getters.isLoggedIn || false;

  // dispatch login if last name is setn in the query
  if (!isAuthenticated && to.query.lastName != undefined) {
    store
      .dispatch("login", {
        regCode: to.query.regCode,
        lastName: to.query.lastName,
        email: to.query.email,
      })
      .then(() => {
        if (store.getters.isLoggedIn) {
          next();
        } else {
          next({
            name: "Login",
            query: { redirect: to.fullPath, name: to.name },
          });
        }
      });
  }

  // Logic if there was not a last name passed in to login immediately
  else if (to.matched.some((record) => record.meta.requiresAuth)) {
    // needs to login
    if (!isAuthenticated) {
      next({ name: "Login", query: { redirect: to.fullPath, name: to.name } });
    } else {
      next();
    }
  } else {
    next();
  }
});

router.afterEach((to) => {
  // Log to analytics
  Analytics.logEvent("aad_page_view", {
    page_name: to.meta.analyticsName || "unknown",
  });

  // Close sidebar no matter what on new page in mobile
  store.state.mobileSidebarOpen = false;
});

export default router;
